// Deep link that opens the Claude desktop app's Cowork feature in a new session,
// pre-filling (not sending) the prompt. `claude://cowork/new` is the documented
// desktop scheme (https://support.claude.com/en/articles/14729294); the web
// `https://claude.ai/code` URL would open Cowork-on-the-web in a browser instead.
// Single source of truth — if the deeplink format moves, this is the only line to change.
export const CLAUDE_COWORK_DEEPLINK_BASE = "claude://cowork/new";

// The org connector (MCP server) the Cowork session uses to fetch the task, load
// the attached skill, and record results. Matches the connector's display name.
const ALPINE_WORKER_CONNECTOR_NAME = "Alpine Worker";

const QUOTEWELL_CONNECTOR_NAME = "QuoteWell";

const QUOTE_UPLOAD_TASK_TYPE_SLUG = "quote-upload";

// Only opaque identifiers go into the deeplink — never human-readable task content.
// The task title/description/skill can carry insured/carrier PII, and the prompt
// rides in a GET URL (browser history, proxy logs), so Claude fetches that content
// through the Alpine Worker connector instead. As a bonus this leaves no
// author-controlled text in the prompt, so there's no prompt-injection surface.
// Keep this interface free of free-text fields.
export interface CoworkTaskInput {
  id: string;
  taskType?: { slug?: string | null } | null;
  opportunityContext?: { opportunityId: string } | null;
}

// Builds the initial Cowork prompt: instructions to fetch the task and load its
// attached skill through the Alpine Worker connector, plus opaque identifiers. For
// quote-upload, also instruct using Claude in Chrome to help fill the carrier portal.
export const buildCoworkPrompt = (task: CoworkTaskInput): string => {
  const steps = [
    `Use the "${ALPINE_WORKER_CONNECTOR_NAME}" connector (MCP server) and its tools to fetch the full task by its Task ID (below) — its title, description, attached skill, files, and related context. The fetched task is the source of truth; record your progress and results back through the connector.`,
    // Phrased conditionally: eligible tasks may have no attached skill, and the web
    // side no longer carries skill presence (only opaque ids reach the deeplink), so
    // let Claude decide from the task it fetches rather than asserting a skill exists.
    `If the task has an attached skill, load it through the ${ALPINE_WORKER_CONNECTOR_NAME} connector and follow its instructions.`,
  ];

  if (task.taskType?.slug === QUOTE_UPLOAD_TASK_TYPE_SLUG) {
    steps.push(
      "Use Claude in Chrome to open the carrier portal and help fill out the quote submission, capturing each screen as you go, then upload the resulting quote or decision back to the task."
    );
  }

  const numberedSteps = steps.map((step, index) => `${index + 1}. ${step}`).join("\n");

  const contextLines = [`- Task ID: ${task.id}`];
  if (task.opportunityContext?.opportunityId) {
    contextLines.push(`- Opportunity ID: ${task.opportunityContext.opportunityId}`);
  }

  return [
    "You are helping a QuoteWell Alpine worker complete an Alpine task.",
    `To complete this task:\n${numberedSteps}`,
    `Context (opaque identifiers — fetch the human-readable task through the connector):\n${contextLines.join("\n")}`,
  ].join("\n\n");
};

// Wraps a prompt in the desktop Cowork deeplink. The prompt rides in the `q`
// parameter (the scheme's documented param), URL-encoded with `encodeURIComponent`
// (not URLSearchParams, which encodes spaces as `+`).
const coworkDeeplink = (prompt: string): string => `${CLAUDE_COWORK_DEEPLINK_BASE}?q=${encodeURIComponent(prompt)}`;

// Builds the deeplink that opens the task in a new desktop Cowork session.
export const buildCoworkDeeplink = (task: CoworkTaskInput): string => coworkDeeplink(buildCoworkPrompt(task));

// Opens a new desktop Cowork session pointed at an opportunity. Only the opaque
// opportunity id rides in the URL; Claude fetches the insured, submissions,
// quotes, conversations, and files through the QuoteWell connector.
export const buildOpportunityCoworkPrompt = (opportunityId: string): string =>
  [
    "You are helping a QuoteWell broker work an insurance opportunity.",
    `To get started:\n1. Use the "${QUOTEWELL_CONNECTOR_NAME}" connector (MCP server) and its getOpportunityContext tool to fetch the full opportunity by its Opportunity ID below — the insured, submissions, quotes, conversations, and attached files.\n2. Open the opportunity's files and use that context to help the broker with whatever they ask next.`,
    `Context (opaque identifier — fetch the human-readable opportunity through the connector):\n- Opportunity ID: ${opportunityId}`,
  ].join("\n\n");

export const buildOpportunityCoworkDeeplink = (opportunityId: string): string =>
  coworkDeeplink(buildOpportunityCoworkPrompt(opportunityId));

// Builds a Cowork deeplink that opens a new desktop session to edit an appetite
// skill from the Authority & Appetite admin page. Only the opaque skill id rides in
// the URL; Claude reads the skill's current content and applies edits through the
// Alpine Worker connector — same opaque-id-only contract as the task deeplink.
export const buildAppetiteSkillEditDeeplink = (skillId: string): string =>
  coworkDeeplink(
    [
      "You are helping a QuoteWell Alpine admin edit an appetite skill.",
      `To edit this skill:\n1. Use the "${ALPINE_WORKER_CONNECTOR_NAME}" connector (MCP server) and its skill tools to fetch the skill by its Skill ID below and read its current instructions/content.\n2. Work with the admin to revise the skill, then save the changes back through the connector's update-skill tool.`,
      `Context (opaque identifier — fetch the skill through the connector):\n- Skill ID: ${skillId}`,
    ].join("\n\n")
  );

// Builds a Cowork deeplink that opens a new desktop session to edit an authority
// grant. Only the opaque grant id rides in the URL; Claude fetches the grant and
// adjusts its authority through the Alpine Worker connector.
export const buildAuthorityGrantEditDeeplink = (grantId: string): string =>
  coworkDeeplink(
    [
      "You are helping a QuoteWell Alpine admin edit an authority grant.",
      `To edit this grant:\n1. Use the "${ALPINE_WORKER_CONNECTOR_NAME}" connector (MCP server) and its authority tools to fetch the grant by its Grant ID below and review its scope and conditions.\n2. Work with the admin to adjust the grant's authority, then apply the change through the connector's authority tools.`,
      `Context (opaque identifier — fetch the grant through the connector):\n- Grant ID: ${grantId}`,
    ].join("\n\n")
  );
